// @flow

import React from 'react';
import {motion, AnimatePresence} from 'framer-motion';
import {useArticle, useAccount, usePersistentState} from 'docs/data';
import Box from 'sail/Box';
import Group from 'sail/Group';

import {defineMessages, FormattedMessage} from 'react-intl';
import {ButtonLink} from 'sail/Button';

const messages = defineMessages({
  header: {
    id: 'docsShell.stripeSessionsBanner.2022.header',
    description: 'Join us label',
    defaultMessage:
      'Join us on May 24 at Sessions—our annual user conference—for product updates, demos, and talks.',
  },
  learnMore: {
    id: 'docsShell.stripeSessionsBanner.2022.learnMore',
    description: 'Learn more',
    defaultMessage: 'Learn more',
  },
});

export const StripeSessionsBanner = () => {
  const {account} = useAccount();
  const {article, isLoading: articleLoading} = useArticle();
  const [dismissed, setDismissed, isLoading] = usePersistentState<boolean>(
    'docs-stripe-sessions-2022-banner-dismissed',
    false,
  );

  if (
    !account.flags.docs_stripe_sessions_2022_banner_active ||
    article?.metadata?.type !== 'landing-page' ||
    articleLoading ||
    isLoading ||
    dismissed
  ) {
    return null;
  }

  return (
    <AnimatePresence exitBeforeEnter>
      <motion.div
        initial={{opacity: 0, y: -100}}
        animate={{opacity: 1, y: 0}}
        exit={{opacity: 0, y: 100}}
        transition={{type: 'spring', damping: 100, stiffness: 500}}
        style={{
          position: 'relative',
          borderRadius: 4,
          padding: '28px 32px 26px',
          margin: '0px -32px 32px',
          background:
            'linear-gradient(270deg, #FCF9FF 0%, #F4F5FE 29.17%, #F1F7FE 65.62%, #EAFCFD 100%)',
        }}
      >
        <Group spacing={8}>
          <Box>
            <svg
              width="162"
              height="23"
              viewBox="0 0 162 23"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M155.879 18.2525C158.763 18.2525 161.173 16.6656 161.173 14.0242C161.173 11.1614 158.536 10.6341 156.385 10.1807C154.614 9.79584 153.633 9.5955 153.633 8.57795C153.633 7.62895 154.54 7.01209 155.858 7.01209C157.366 7.01209 158.42 7.73966 159.211 8.8152L161.041 7.0859C160.097 5.77311 158.273 4.77139 155.937 4.77139C153.132 4.77139 150.907 6.33725 150.907 8.83629C150.907 11.425 153.09 12.0471 155.109 12.5005C157.145 12.9381 158.426 13.1015 158.426 14.3036C158.426 15.4161 157.387 16.0171 155.979 16.0171C154.493 16.0171 153.043 15.2526 152.041 13.8871L150.138 15.6744C151.229 17.2508 153.602 18.2525 155.879 18.2525ZM138.739 18.0522V9.03137L145.071 18.0522H147.481V4.96646H144.823V13.5497L138.887 4.96646H136.082V18.0522H138.739ZM126.365 15.9011C124.235 15.9011 122.748 14.1876 122.748 11.5093C122.748 8.83102 124.235 7.11754 126.365 7.11754C128.474 7.11754 129.961 8.83102 129.961 11.5093C129.961 14.1876 128.474 15.9011 126.365 15.9011ZM126.365 18.2525C130.077 18.2525 132.75 15.4635 132.75 11.5093C132.75 7.55514 130.077 4.76611 126.365 4.76611C122.638 4.76611 119.959 7.55514 119.959 11.5093C119.959 15.4635 122.632 18.2525 126.365 18.2525ZM116.643 18.0522V4.96646H113.875V18.0522H116.643ZM105.397 18.2525C108.281 18.2525 110.691 16.6656 110.691 14.0242C110.691 11.1614 108.055 10.6341 105.904 10.1807C104.132 9.79584 103.151 9.5955 103.151 8.57795C103.151 7.62895 104.053 7.01209 105.376 7.01209C106.884 7.01209 107.939 7.73966 108.729 8.8152L110.559 7.0859C109.615 5.77311 107.791 4.77139 105.455 4.77139C102.651 4.77139 100.426 6.33725 100.426 8.83629C100.426 11.425 102.608 12.0471 104.628 12.5005C106.663 12.9381 107.944 13.1015 107.944 14.3036C107.944 15.4161 106.905 16.0171 105.498 16.0171C104.011 16.0171 102.561 15.2526 101.559 13.8871L99.6559 15.6744C100.747 17.2508 103.12 18.2525 105.397 18.2525ZM92.3855 18.2525C95.2694 18.2525 97.6788 16.6656 97.6788 14.0242C97.6788 11.1614 95.0427 10.6341 92.8916 10.1807C91.1201 9.79584 90.1395 9.5955 90.1395 8.57795C90.1395 7.62895 91.0411 7.01209 92.3644 7.01209C93.8723 7.01209 94.9267 7.73966 95.7175 8.8152L97.547 7.0859C96.6033 5.77311 94.7791 4.77139 92.4435 4.77139C89.6386 4.77139 87.4138 6.33725 87.4138 8.83629C87.4138 11.425 89.5965 12.0471 91.6157 12.5005C93.6508 12.9381 94.932 13.1015 94.932 14.3036C94.932 15.4161 93.8933 16.0171 92.4857 16.0171C90.9989 16.0171 89.549 15.2526 88.5473 13.8871L86.644 15.6744C87.7301 17.2508 90.1026 18.2525 92.3855 18.2525ZM84.4138 18.0522V15.7377H77.8024V12.4952H83.3383V10.1965H77.8024V7.28098H84.4138V4.96646H75.0714V18.0522H84.4138ZM66.5989 18.2525C69.4828 18.2525 71.8922 16.6656 71.8922 14.0242C71.8922 11.1614 69.2561 10.6341 67.105 10.1807C65.3336 9.79584 64.3529 9.5955 64.3529 8.57795C64.3529 7.62895 65.2598 7.01209 66.5778 7.01209C68.0857 7.01209 69.1401 7.73966 69.931 8.8152L71.7604 7.0859C70.8167 5.77311 68.9925 4.77139 66.6569 4.77139C63.8521 4.77139 61.6272 6.33725 61.6272 8.83629C61.6272 11.425 63.8099 12.0471 65.8292 12.5005C67.8642 12.9381 69.1454 13.1015 69.1454 14.3036C69.1454 15.4161 68.1068 16.0171 66.6991 16.0171C65.2123 16.0171 63.7624 15.2526 62.7607 13.8871L60.8574 15.6744C61.9435 17.2508 64.316 18.2525 66.5989 18.2525Z"
                fill="#635BFF"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M9.4057 14.0716C9.4057 16.7394 7.32843 18.2578 4.24943 18.2578C2.97355 18.2578 1.61858 18.01 0.242524 17.4195V13.8818C1.48677 14.5567 3.02627 15.0628 4.24943 15.0628C5.07718 15.0628 5.62549 14.8414 5.62549 14.156C5.63076 12.3898 0 13.0541 0 8.95756C0 6.33725 2.04563 4.76612 5.05082 4.76612C6.27925 4.76612 7.50241 4.95592 8.73085 5.44624V8.9312C7.60259 8.32489 6.17381 7.97692 5.04554 7.97692C4.27052 7.97692 3.7433 8.20363 3.7433 8.7783C3.7433 10.4443 9.4057 9.6535 9.4057 14.0716ZM13.8186 13.439C13.8186 15.6217 16.1542 14.9416 16.6287 14.7518V17.7675C16.1331 18.0364 15.2421 18.2578 14.0295 18.2578C11.8309 18.2578 10.1807 16.6392 10.1807 14.446L10.1965 2.59922L13.8133 1.82947L13.8186 5.01391H16.6287V8.17199H13.8186V13.439ZM21.5108 9.19481V17.9942H17.8097V5.01391H21.1259L21.2419 6.11054C22.1435 4.51832 24.0046 4.83993 24.5002 5.01919V8.42506C24.0256 8.26689 22.4387 8.03491 21.5108 9.19481ZM25.3912 0.790838L29.1081 0V3.021L25.3912 3.81184V0.790838ZM25.3912 5.01391H29.1081V17.9942H25.3912V5.01391ZM35.9673 8.04546C35.1185 8.04546 34.586 8.35652 34.2011 8.7783L34.2222 14.2825C34.5807 14.6727 35.1026 14.9837 35.9673 14.9837C37.3381 14.9837 38.2555 13.4917 38.2555 11.4988C38.2555 9.56387 37.3223 8.04546 35.9673 8.04546ZM36.8267 18.2578C35.5297 18.2578 34.7389 17.7095 34.2064 17.3194L34.2011 21.4792L30.5 22.2647V5.00864H33.8742L33.9533 5.92601C34.4963 5.45151 35.4032 4.76612 36.853 4.76612C39.4523 4.76612 41.9039 7.107 41.9039 11.4197C41.8986 16.1226 39.4786 18.2578 36.8267 18.2578ZM48.3096 7.78712C47.3817 7.78712 46.3536 8.44088 46.3536 10.1122H50.1813C50.1813 8.44088 49.2165 7.78712 48.3096 7.78712ZM53.6346 12.8432H46.3853C46.5487 14.5778 47.8246 15.1366 49.2639 15.1366C50.7349 15.1366 51.9211 14.8098 52.9018 14.2825V17.2666C51.8948 17.9046 50.5662 18.2631 48.7947 18.2631C45.1885 18.2631 42.6578 16.0066 42.6578 11.5357C42.6578 7.76603 44.8036 4.77139 48.3255 4.77139C51.842 4.77139 53.6768 7.72385 53.6768 11.5146C53.6873 11.8678 53.6504 12.6429 53.6346 12.8432Z"
                fill="#0A2540"
              />
            </svg>
          </Box>
          <Box>
            <FormattedMessage {...messages.header} />{' '}
            <ButtonLink
              href="https://sessions.stripe.com/?utm_medium=owned&utm_source=ccf5&utm_campaign=GLOBAL_4f20&utm_content=80bb&utm_term=37ac837e3ce9"
              label={<FormattedMessage {...messages.learnMore} />}
              icon="arrowRight"
              iconPosition="right"
              display="inline-flex"
            />
          </Box>
        </Group>
        <Box style={{position: 'absolute', top: 24, right: 24}}>
          <ButtonLink
            label="Close"
            icon="delete"
            hideLabel
            color="gray"
            size="small"
            onClick={() => setDismissed(true)}
          />
        </Box>
      </motion.div>
    </AnimatePresence>
  );
};
